import React from 'react'
import styled from '@emotion/styled'
import { animate } from 'animejs'
import { useRenderTimeoutControl } from '@mobi/component-library/Utils/hooks/useRenderTimeoutControl'

export const AnimatedHeightToggle: React.FC<
  React.PropsWithChildren & {
    isExpanded: boolean
    timeoutMs?: number
    testId?: string
  }
> = ({ children, isExpanded, timeoutMs = 400, testId }) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)

  const isExpandedOnMountRef = React.useRef(isExpanded)

  const shouldRender = useRenderTimeoutControl({ shouldRender: isExpanded, timeoutMs })

  if (isExpandedOnMountRef.current && !isExpanded) {
    isExpandedOnMountRef.current = false
  }

  React.useEffect(() => {
    if (!contentRef.current || !wrapperRef.current || isExpandedOnMountRef.current) return undefined

    const wrapperEl = wrapperRef.current
    const contentEl = contentRef.current

    const heightKeyframes = [0, contentEl.offsetHeight]
    const wrapperAnimate = animate(wrapperEl, {
      height: isExpanded ? heightKeyframes : heightKeyframes.toReversed(),
      duration: timeoutMs,
      onComplete() {
        if (isExpanded) wrapperEl.style.height = 'auto'
      },
    })

    contentEl.style.opacity = isExpanded ? '1' : '0'

    return () => {
      wrapperAnimate.revert()
    }
  }, [isExpanded, timeoutMs])

  if (!shouldRender && !isExpanded) return null

  return (
    <WrapperStyled
      ref={wrapperRef}
      data-testid={testId}
      data-use-child-opacity={!isExpandedOnMountRef.current}
    >
      <div ref={contentRef}>{children}</div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  position: 'relative',
  willChange: 'height',
  overflow: 'hidden',

  '@supports (overflow: clip)': {
    overflow: 'clip',
    overflowClipMargin: '1px',
  },

  '&[data-use-child-opacity="true"] > div': {
    opacity: 0,
    transition: 'opacity 400ms ease',
  },
})
